import React, { useState, useEffect, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { Typography, Grid, Stack, Box, Divider, Input, IconButton, InputAdornment, Button } from "@mui/material"
import SendIcon from "@mui/icons-material/Send"
import BookTwoToneIcon from "@mui/icons-material/BookTwoTone"
import UserContext from "../../../context/UserContext"
import ChatDialog from "./ChatDialog"
import useFetch from "../../../hooks/useFetch"
import useMutation from "../../../hooks/useMutation"
import { F_TRADING_HISTORY } from "../../../constants/routes"
import BackdropContext from "../../../context/BackdropContext"
import StateContext from "../../../context/StateContext"

const CurrentChatDesktop = ({ currentChat, setCurrentChat, setSnackbarMsg }) => {
  const navigate = useNavigate()

  const { backdropOpen, openBackdrop, closeBackdrop } = useContext(BackdropContext)
  const { userData } = useContext(UserContext)
  const userId = userData.user.id
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showFertigDialog, setShowFertigDialog] = useState(false)
  const [msg, setMsg] = useState("")

  const [chatMessages, setChatMessages] = useState([])

  const { mutate: sendMutation, loading: sendLoading } = useMutation(`/chat/${currentChat?.chatId}`)
  const { mutate: cancelMutation, loading: cancelLoading } = useMutation("/ledger", "DELETE")
  const { mutate: finishMutation, loading: finishLoading } = useMutation("/ledger/finish")

  const { ledgerRefetch } = useContext(StateContext)

  const { data: chatMessagesRaw, loading: chatMessagesLoading } = useFetch(`/chat/${currentChat?.chatId}`, {
    skip: !currentChat || !currentChat?.chatId,
  })

  const loading = cancelLoading || finishLoading || chatMessagesLoading

  useEffect(() => {
    if (loading) {
      openBackdrop()
    } else {
      if (backdropOpen) {
        closeBackdrop()
      }
    }
  }, [loading, openBackdrop, backdropOpen, closeBackdrop])

  useEffect(() => {
    if (chatMessagesRaw && !chatMessagesLoading) {
      setChatMessages(JSON.parse(chatMessagesRaw.messages))
    } else {
      setChatMessages([])
    }
  }, [chatMessagesRaw, chatMessagesLoading])

  const handleFinish = async (preis) => {
    if (!currentChat) {
      return
    }

    try {
      await finishMutation({
        ...currentChat, // just for backend validation
        id: currentChat.id, // only these fields are really needed
        priceNegotiated: preis,
      })

      navigate(F_TRADING_HISTORY)
      setSnackbarMsg("Abgeschlossen!")
    } catch (e) {
      console.log(e)
    }

    setCurrentChat(null)
    ledgerRefetch()
  }

  const handleCancelPosition = async () => {
    if (!currentChat) {
      return
    }

    try {
      await cancelMutation({
        ...currentChat, // just for backend validation
        id: currentChat.id, // only these fields are really needed
      })

      const txt = currentChat.action === "buy" ? "Kauf" : "Verkauf"
      setSnackbarMsg(`${txt} abgebrochen`)
    } catch (e) {
      console.log(e)
    }

    setCurrentChat(null)
    ledgerRefetch()
  }

  const cancelDialog = () => {
    setShowFertigDialog(false)
    setShowCancelDialog(false)
  }

  const handleSend = async () => {
    setMsg("")

    try {
      const res = await sendMutation({
        senderId: userId,
        text: msg,
        // timestamp: Date.now().toLocaleString(),
      })

      if (res.messages) {
        setChatMessages(JSON.parse(res.messages))
      }
    } catch (e) {
      console.log(e.message)
    }
  }

  return (
    <>
      <Grid container direction="column" justifyContent="space-between" sx={{ height: "100%", pb: 1 }}>
        <Grid xs="auto" item container justifyContent="space-between" sx={{ height: "8vh", display: "inline-flex", mt: "2vh" }}>
          <Grid item xs="auto">
            <BookTwoToneIcon fontSize="large" sx={{ color: currentChat.status === "matched" ? "primary.main" : "inherit" }} />
          </Grid>
          <Grid item xs>
            <Typography variant="h5" sx={{ cursor: "default", color: currentChat.status === "matched" ? "inherit" : "grey" }}>
              {currentChat.book.title} {currentChat.status === "matched" ? "" : "• Fertig"}
            </Typography>
          </Grid>
          <Grid item xs>
            {currentChat.status === "matched" && (
              <Box sx={{ float: "right" }}>
                <Button onClick={() => setShowFertigDialog(true)} variant="contained" sx={{ mr: "2vw" }} disableElevation>
                  Abschließen
                </Button>
                <Button
                  onClick={() => setShowCancelDialog(true)}
                  variant="outlined"
                  sx={{ width: "10vw", color: "red", borderColor: "red", "&:hover": { borderColor: "red", backgroundColor: "salmon" } }}>
                  Abbrechen
                </Button>
              </Box>
            )}
            {currentChat.status === "finished" && !!currentChat.priceNegotiated && (
              <Typography variant="h5" align="right" sx={{ color: "grey" }}>
                {currentChat?.priceNegotiated + " €"}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Grid item xs="auto">
          <Divider sx={{ mt: "-2vh" }} />
        </Grid>

        <Grid item xs sx={{ overflowY: "hidden" }}>
          <Stack
            id="chat"
            sx={{
              overflowY: "auto",
              overflowX: "hidden",
              // height: currentChat.status === "finished" ? "81vh" : "75vh",
              height: "100%",
              mt: "1vh",
              pr: "1vw",
            }}>
            {!loading && !chatMessages.length && (
              <Box sx={{ backgroundColor: "#eee", mt: 2, p: 2 }}>
                <Typography textAlign="left">Hier kannst du mit deinem Tausch-Partner schreiben</Typography>
                <Typography sx={{ mt: 2 }} textAlign="left">
                  Sobald du mit dem {currentChat.action === "buy" ? "Kauf" : "Verkauf"} fertig bist, klick auf "Abschließen"
                </Typography>
                <Typography sx={{ mt: 1 }} textAlign="left">
                  Wenn dein Tausch-Partner nicht reagiert oder sonstige Probleme auftreten, klick auf "Abbrechen"
                </Typography>
              </Box>
            )}

            {chatMessages.map((msg, index) => (
              <Typography
                key={index}
                align={userId === msg.SenderId ? "right" : "left"}
                sx={{
                  wordWrap: "break-word",
                  border: "1px solid gray",
                  borderRadius: "20px",
                  borderBottomLeftRadius: userId === msg.SenderId ? "20px" : "0",
                  borderBottomRightRadius: userId === msg.SenderId ? "0" : "20px",
                  height: "auto",
                  width: "auto",
                  maxWidth: "60%",
                  py: "1vh",
                  px: 2,
                  my: "1vh",
                  ml: userId === msg.SenderId ? "auto" : "0",
                  mr: userId === msg.SenderId ? "0" : "auto",
                  backgroundColor: userId === msg.SenderId ? "primary.main" : "#052D4E",
                  color: "#FFF",
                }}>
                {msg.Text}
              </Typography>
            ))}
          </Stack>
        </Grid>

        <Grid item xs="auto">
          <Input
            autoFocus
            sx={{
              border: "2px solid",
              borderColor: currentChat.status === "finished" || msg.length <= 0 || msg.trim() === "" ? "grey" : "primary.main",
              borderRadius: "20px",
              height: "5vh",
              mt: "1vh",
              p: "1vh",
              visibility: currentChat.status === "finished" ? "hidden" : "visible",
            }}
            fullWidth
            id="msg"
            name="msg"
            onChange={(e) => setMsg(e.target.value)}
            value={msg}
            onKeyDown={(e) => {
              if (e.key === "Enter" && msg.trim().length > 0) {
                handleSend()
              }
            }}
            variant="standard"
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={() => msg.trim().length > 0 && handleSend()}>
                  <SendIcon sx={{ color: currentChat.status === "finished" || msg.length <= 0 || msg.trim() === "" ? "grey" : "primary.main" }} />
                </IconButton>
              </InputAdornment>
            }
            disableUnderline
            disabled={sendLoading || currentChat.status === "finished"}
          />
        </Grid>
      </Grid>

      {(showFertigDialog || showCancelDialog) && (
        <ChatDialog
          showFertigDialog={showFertigDialog}
          showCancelDialog={showCancelDialog}
          handleCancelPosition={handleCancelPosition}
          cancelDialog={cancelDialog}
          handleFinish={handleFinish}
          currentChat={currentChat}
        />
      )}
    </>
  )
}

export default CurrentChatDesktop
